import { isTemplateAllowed } from '@electron/shared/mcp-template-gating';
import {
  type CachedMCPServer,
  hasConnectedMcpStatus,
  type MCPCatalogEntry,
} from '@littlebird/mcp';
import { useMemo } from 'react';

import { type IconName } from '@/assets/icons/icons';
import { FEATURES, useFeature } from '@/features/feature-flags';
import { displayableLabel } from '@/features/integration-management/integration-management.adapters';
import {
  getBundledMcpTemplates,
  type BundledMcpTemplate,
} from '@/features/integrations/bundled-mcp-templates';
import { INTEGRATION_CATEGORIES } from '@/features/integrations/integration-categories';
import {
  useDismissIntegrationSuggestionMutation,
  useIntegrationSuggestionsQuery,
  useOnDeviceMcpCatalogQuery,
} from '@/features/integrations/integrations.query';
import {
  ON_DEVICE_SELECTION,
  SUGGESTED_SELECTION,
  supportedOnThisDevice,
  type CachedConnectedAccount,
  type IntegrationState,
  type IntegrationSuggestion,
  type IntegrationTarget,
  type OnDeviceMcpCatalogEntry,
} from '@/features/integrations/integrations.types';
import { useMCPIntegrationState } from '@/features/mcp/mcp.hooks';
import { useChannelConnectionQuery } from '@/features/msg-channels/common/msg-channels.query';
import {
  MESSAGING_CHANNEL_DESCRIPTIONS,
  MESSAGING_CHANNEL_LABELS,
  MessagingChannelTypes,
  type MessagingChannelType,
} from '@/features/msg-channels/common/msg-channels.types';
import { useMcpServersQuery } from '@/features/settings/sub-settings/settings-local-mcp.query';
import {
  GUIDED_CUSTOM_MCP_OFFERS,
  type GuidedCustomMcpOffer,
} from '@/integrations/custom/guided-custom-mcp';
import { useIntegrationState } from '@/integrations/useIntegrationState';
import { trackEvent } from '@/utils/analytics';
import { inDesktop } from '@/utils/capabilities';

import type { ServerEntry } from '@littlebird/axon-mcp';

export type GridItem = (
  | { type: 'integration'; integration: IntegrationState }
  | {
      type: 'mcp_server';
      // The group's representative account, shown on the card
      server: CachedMCPServer;
      accounts: CachedMCPServer[];
      description?: string;
      iconUrl?: string | null;
    }
  | { type: 'mcp_catalog'; entry: MCPCatalogEntry }
  | {
      type: 'local_mcp_template';
      entry: OnDeviceMcpCatalogEntry;
      installedServer?: ServerEntry;
    }
  | {
      type: 'bundled_mcp_template';
      template: BundledMcpTemplate;
      installedServer?: ServerEntry;
    }
  | { type: 'guided_custom_mcp'; offer: GuidedCustomMcpOffer }
  | { type: 'channel'; channelType: MessagingChannelType }
) & {
  categories: string[];
  isConnected: boolean;
  searchText: string;
  // Set on the copy of an item in the Suggested section: why it is suggested, and what Dismiss quiets
  suggestion?: IntegrationSuggestion;
};

/** One account whose authorization lapsed, and the target that re-authorizes exactly it */
export type ReconnectAccount = {
  id: string;
  displayName: string;
  // Empty unless the integration has several accounts, as on Home's reconnect cards
  accountLabel: string;
  iconKey: string;
  iconUrl?: string | null;
  target: IntegrationTarget;
};

const composioAccountNeedsReconnect = (account: CachedConnectedAccount) =>
  account.is_user_enabled &&
  (account.connection_status === 'failed' ||
    account.connection_status === 'expired' ||
    account.connection_status === 'cancelled');

const mcpAccountNeedsReconnect = (server: CachedMCPServer) =>
  server.enabled &&
  server.auth_type === 'oauth' &&
  (server.status === 'expired' || server.status === 'cancelled');

export const reconnectAccounts = (items: GridItem[]): ReconnectAccount[] =>
  items.flatMap((item): ReconnectAccount[] => {
    if (item.type === 'integration') {
      const { info, accounts } = item.integration;
      const hasSeveralAccounts = accounts.length > 1;
      return accounts.filter(composioAccountNeedsReconnect).map((account) => ({
        id: account.id,
        displayName: info.display_name,
        accountLabel: hasSeveralAccounts
          ? displayableLabel(account.account_label)
          : '',
        iconKey: info.toolkit_name,
        target: {
          kind: 'integration',
          toolkitName: info.toolkit_name,
          displayName: info.display_name,
          requiresPaidPlan: info.requires_paid_plan,
          reconnectAccountId: account.id,
        },
      }));
    }
    if (item.type !== 'mcp_server') return [];
    const hasSeveralAccounts = item.accounts.length > 1;
    return item.accounts.filter(mcpAccountNeedsReconnect).map((server) => ({
      id: server.id,
      displayName: server.name,
      accountLabel: hasSeveralAccounts
        ? displayableLabel(server.account_label)
        : '',
      iconKey: server.name,
      iconUrl: item.iconUrl,
      target: {
        kind: 'mcp',
        displayName: server.name,
        // Every remote MCP server is a paid feature (integrations_access_policy.py)
        requiresPaidPlan: true,
        reconnectServerId: server.id,
      },
    }));
  });

const isOnDeviceGridItem = (gridItem: GridItem): boolean =>
  gridItem.type === 'local_mcp_template' ||
  gridItem.type === 'bundled_mcp_template' ||
  (gridItem.type === 'integration' &&
    (gridItem.integration.info.on_device ?? false));

const channelGridItem = (
  channelType: MessagingChannelType,
  isConnected: boolean
): GridItem => ({
  type: 'channel',
  channelType,
  categories: ['communication'],
  isConnected,
  searchText:
    `${MESSAGING_CHANNEL_LABELS[channelType]} ${MESSAGING_CHANNEL_DESCRIPTIONS[channelType]}`.toLowerCase(),
});

export type StatusFilter = 'all' | 'connected' | 'not_connected';

// 'suggested' and 'all' are pseudo-categories; everything else is a category slug
export type Selection = typeof SUGGESTED_SELECTION | 'all' | string;

export type GridSection = {
  slug: string;
  label: string;
  icon: IconName;
  items: GridItem[];
};

const matchesSuggestion = (
  item: GridItem,
  suggestion: IntegrationSuggestion
): boolean => {
  switch (suggestion.provider) {
    case 'composio':
      return (
        item.type === 'integration' &&
        item.integration.info.toolkit_name === suggestion.integration_id
      );
    case 'on_device':
      return (
        (item.type === 'local_mcp_template' &&
          item.entry.id === suggestion.toolkit_slug) ||
        (item.type === 'bundled_mcp_template' &&
          item.template.id === suggestion.toolkit_slug) ||
        (item.type === 'integration' &&
          item.integration.info.toolkit_name === suggestion.toolkit_slug)
      );
    case 'mcp':
      return (
        item.type === 'mcp_catalog' && item.entry.id === suggestion.toolkit_slug
      );
  }
};

// The same rows Home shows, in Home's order, as copies of the grid items they resolve to.
// Reconnect entries stay on Home: the page has its own "Needs re-connection" section.
const suggestedGridItems = (
  suggestions: IntegrationSuggestion[],
  items: GridItem[]
): GridItem[] =>
  suggestions.flatMap((suggestion) => {
    if (suggestion.action !== 'connect') return [];
    const item = items.find((candidate) =>
      matchesSuggestion(candidate, suggestion)
    );
    if (suggestion.provider === 'on_device' && item?.isConnected) return [];
    return item ? [{ ...item, suggestion }] : [];
  });

export const useIntegrationGrid = ({
  search,
  filter,
  selection,
  includeDesktopOnly = false,
  includeMessagingChannels = false,
}: {
  search: string;
  filter: StatusFilter;
  selection: Selection;
  includeDesktopOnly?: boolean;
  /** Includes messaging channel cards only when a caller explicitly opts in */
  includeMessagingChannels?: boolean;
}) => {
  const hasFeature = useFeature();
  const customMcpEnabled = hasFeature(FEATURES.CUSTOM_MCP_INTEGRATIONS);
  const localMcpCatalogEnabled =
    (inDesktop() || includeDesktopOnly) &&
    hasFeature(FEATURES.CUSTOM_LOCAL_MCP);
  const localMcpRuntimeEnabled =
    inDesktop() && hasFeature(FEATURES.CUSTOM_LOCAL_MCP);
  const suggestionsEnabled = hasFeature(FEATURES.HOME_INTEGRATION_SUGGESTIONS);
  const suggestionsQuery = useIntegrationSuggestionsQuery(suggestionsEnabled);
  // Never read Home's cached rows past the flag
  const suggestions = suggestionsEnabled ? suggestionsQuery.data : undefined;
  const dismissMutation = useDismissIntegrationSuggestionMutation();

  const {
    availableIntegrations,
    connectedAccounts,
    integrationStates,
    loadError: integrationsLoadError,
    refetchAvailableIntegrations,
    refetchConnectedAccounts,
  } = useIntegrationState(includeDesktopOnly);

  const {
    mcpEnabled,
    catalogWithServers,
    customMCPServers,
    isLoading: isMCPLoading,
    loadError: mcpLoadError,
    refetch: refetchMCPState,
  } = useMCPIntegrationState();

  const onDeviceCatalogData = useOnDeviceMcpCatalogQuery(
    localMcpCatalogEnabled,
    includeDesktopOnly
  ).data;
  // Client-side gates on top of the backend catalog's own feature_flag install
  // door: a template gated in MCP_TEMPLATE_FEATURE_FLAGS disappears here too,
  // and entries with a platforms list only show on those platforms (the backend
  // can't filter — request headers don't distinguish macOS from Windows)
  const onDeviceCatalog = useMemo(
    () =>
      localMcpCatalogEnabled
        ? (onDeviceCatalogData ?? []).filter(
            (entry) =>
              isTemplateAllowed(entry.id, hasFeature) &&
              (includeDesktopOnly || supportedOnThisDevice(entry.platforms))
          )
        : [],
    [
      localMcpCatalogEnabled,
      onDeviceCatalogData,
      hasFeature,
      includeDesktopOnly,
    ]
  );
  const localServersData = useMcpServersQuery(localMcpRuntimeEnabled).data;
  const localServers = useMemo(
    () => localServersData ?? [],
    [localServersData]
  );
  const bundledTemplates = useMemo(
    () =>
      localMcpCatalogEnabled
        ? getBundledMcpTemplates(includeDesktopOnly).filter((template) =>
            isTemplateAllowed(template.id, hasFeature)
          )
        : [],
    [includeDesktopOnly, localMcpCatalogEnabled, hasFeature]
  );

  // Reads the same connection query the channel's rail row/settings page use
  // (shared query key) — no extra polling, just this grid's own subscription.
  const imessageConnectionQuery = useChannelConnectionQuery(
    MessagingChannelTypes.IMESSAGE,
    { enabled: includeMessagingChannels }
  );
  const whatsappConnectionQuery = useChannelConnectionQuery(
    MessagingChannelTypes.WHATSAPP_BUSINESS,
    { enabled: includeMessagingChannels }
  );
  const messagingChannelItems = useMemo<GridItem[]>(() => {
    const items: GridItem[] = [];
    // Wait for the connection query to resolve so a cold cache never renders
    // a connected channel with the Connect action.
    if (includeMessagingChannels && imessageConnectionQuery.isSuccess) {
      items.push(
        channelGridItem(
          MessagingChannelTypes.IMESSAGE,
          Boolean(imessageConnectionQuery.data?.identifier)
        )
      );
    }
    if (includeMessagingChannels && whatsappConnectionQuery.isSuccess) {
      items.push(
        channelGridItem(
          MessagingChannelTypes.WHATSAPP_BUSINESS,
          Boolean(whatsappConnectionQuery.data?.identifier)
        )
      );
    }
    return items;
  }, [
    includeMessagingChannels,
    imessageConnectionQuery.isSuccess,
    imessageConnectionQuery.data,
    whatsappConnectionQuery.isSuccess,
    whatsappConnectionQuery.data,
  ]);

  // Only the Composio load blanks the whole grid — an MCP-only failure shows an
  // inline, retryable banner so the rest of the catalog stays usable.
  const loadError = integrationsLoadError;
  const isLoading =
    !loadError &&
    (!availableIntegrations || !connectedAccounts || isMCPLoading);

  const retryLoad = () => {
    refetchAvailableIntegrations();
    refetchConnectedAccounts();
    refetchMCPState();
  };

  const allItems = useMemo<GridItem[]>(() => {
    const items: GridItem[] = [];

    for (const integration of integrationStates) {
      items.push({
        type: 'integration',
        integration,
        categories: integration.info.categories?.length
          ? integration.info.categories
          : ['other'],
        isConnected: integration.isConnected,
        searchText:
          `${integration.info.display_name} ${integration.info.description} ${integration.info.toolkit_name}`.toLowerCase(),
      });
    }

    for (const { entry, group } of catalogWithServers) {
      const categories = entry.categories?.length
        ? entry.categories
        : ['other'];
      if (group) {
        const server = group.representative;
        items.push({
          type: 'mcp_server',
          server,
          accounts: group.accounts,
          description: entry.description,
          iconUrl: entry.icon_url,
          categories,
          isConnected: hasConnectedMcpStatus(server),
          searchText: `${server.name} ${entry.description}`.toLowerCase(),
        });
      } else {
        items.push({
          type: 'mcp_catalog',
          entry,
          categories,
          isConnected: false,
          searchText: `${entry.name} ${entry.description}`.toLowerCase(),
        });
      }
    }

    if (customMcpEnabled) {
      for (const group of customMCPServers) {
        const server = group.representative;
        items.push({
          type: 'mcp_server',
          server,
          accounts: group.accounts,
          iconUrl: server.icon_url || undefined,
          categories: ['other'],
          isConnected: hasConnectedMcpStatus(server),
          searchText: `${server.name} ${server.url}`.toLowerCase(),
        });
      }
    }

    // Guided custom MCPs (e.g. Zoho CRM) connect via a bespoke flow rather than
    // a shared catalog URL, so they're injected from a registry instead of the
    // catalog. Hidden once their matching server is already connected.
    if (mcpEnabled && customMcpEnabled) {
      const customServers = customMCPServers.flatMap((group) => group.accounts);
      for (const offer of GUIDED_CUSTOM_MCP_OFFERS) {
        if (offer.isConnected(customServers)) continue;
        items.push({
          type: 'guided_custom_mcp',
          offer,
          categories: offer.categories,
          isConnected: false,
          searchText: offer.searchText,
        });
      }
    }

    for (const entry of onDeviceCatalog) {
      const installedServer = localServers.find(
        (server) => server.template === entry.id
      );
      items.push({
        type: 'local_mcp_template',
        entry,
        installedServer,
        categories: entry.categories.length ? entry.categories : ['other'],
        isConnected: Boolean(installedServer),
        searchText: `${entry.name} ${entry.description}`.toLowerCase(),
      });
    }

    for (const template of bundledTemplates) {
      const installedServer = localServers.find(
        (server) => server.template === template.id
      );
      items.push({
        type: 'bundled_mcp_template',
        template,
        installedServer,
        categories: template.categories.length
          ? template.categories
          : ['other'],
        isConnected: Boolean(installedServer),
        searchText: `${template.name} ${template.description}`.toLowerCase(),
      });
    }

    items.push(...messagingChannelItems);

    return items;
  }, [
    integrationStates,
    catalogWithServers,
    customMCPServers,
    customMcpEnabled,
    mcpEnabled,
    onDeviceCatalog,
    localServers,
    bundledTemplates,
    messagingChannelItems,
  ]);

  const statusFiltered = useMemo(
    () =>
      filter === 'all'
        ? allItems
        : allItems.filter((item) =>
            filter === 'connected' ? item.isConnected : !item.isConnected
          ),
    [allItems, filter]
  );

  const suggestedItems = useMemo(
    () => suggestedGridItems(suggestions ?? [], statusFiltered),
    [suggestions, statusFiltered]
  );

  // Sections mirror the sidebar rail: suggested, on-device, then categories in
  // display order. Items with several categories appear in each of them.
  const sections = useMemo<GridSection[]>(() => {
    const result: GridSection[] = [];
    if (suggestedItems.length) {
      result.push({
        slug: SUGGESTED_SELECTION,
        label: 'Suggested',
        icon: 'sparkles',
        items: suggestedItems,
      });
    }
    if (localMcpCatalogEnabled) {
      const onDeviceItems = statusFiltered.filter(isOnDeviceGridItem);
      if (onDeviceItems.length) {
        result.push({
          slug: ON_DEVICE_SELECTION,
          label: 'On device',
          icon: 'plugZap',
          items: onDeviceItems,
        });
      }
    }
    for (const category of INTEGRATION_CATEGORIES) {
      const categoryItems = statusFiltered.filter((item) =>
        item.categories.includes(category.slug)
      );
      if (categoryItems.length) {
        result.push({ ...category, items: categoryItems });
      }
    }
    return result;
  }, [statusFiltered, suggestedItems, localMcpCatalogEnabled]);

  const presentCategories = useMemo(() => {
    const present = new Set<string>();
    for (const item of allItems) {
      for (const slug of item.categories) present.add(slug);
    }
    return INTEGRATION_CATEGORIES.filter((category) =>
      present.has(category.slug)
    );
  }, [allItems]);

  const hasSuggested = suggestedItems.length > 0;
  const hasOnDevice =
    localMcpCatalogEnabled && allItems.some(isOnDeviceGridItem);

  const fallback: Selection = hasSuggested ? SUGGESTED_SELECTION : 'all';
  const resolveEffectiveSelection = (): Selection => {
    if (selection === 'all') return 'all';
    if (selection === ON_DEVICE_SELECTION)
      return hasOnDevice ? ON_DEVICE_SELECTION : fallback;
    if (selection === SUGGESTED_SELECTION) return fallback;
    const isKnownCategory = presentCategories.some(
      (category) => category.slug === selection
    );
    return isKnownCategory ? selection : fallback;
  };
  const effectiveSelection: Selection = resolveEffectiveSelection();

  const bySelection = useMemo(() => {
    if (effectiveSelection === SUGGESTED_SELECTION) return suggestedItems;
    return statusFiltered.filter((item) => {
      if (effectiveSelection === 'all') return true;
      if (effectiveSelection === ON_DEVICE_SELECTION)
        return isOnDeviceGridItem(item);
      return item.categories.includes(effectiveSelection);
    });
  }, [statusFiltered, suggestedItems, effectiveSelection]);

  const query = search.toLowerCase().trim();
  const visibleItems = useMemo(
    () =>
      query
        ? statusFiltered.filter((item) => item.searchText.includes(query))
        : bySelection,
    [statusFiltered, bySelection, query]
  );

  // Quiets the toolkit on Home too: both read the same query
  const dismissSuggestion = (suggestion: IntegrationSuggestion) => {
    trackEvent('INTEGRATIONS_SUGGESTION_DISMISSED', {
      integration: suggestion.toolkit_slug,
      source: suggestion.source,
    });
    dismissMutation.mutate(suggestion.toolkit_slug);
  };

  return {
    isLoading,
    loadError,
    mcpLoadError,
    retryLoad,
    retryMCP: refetchMCPState,
    mcpEnabled,
    customMcpEnabled,
    // Every item, before the status filter, the selection, and the search
    allItems,
    visibleItems,
    sections,
    presentCategories,
    effectiveSelection,
    hasSuggested,
    hasOnDevice,
    dismissSuggestion,
  };
};
